<template>
    <section
        class="text-block"
        :class="componentClasses"
    >
        <div
            v-for="card in cards"
            :key="card.id"
            class="inner"
            :class="{
                [`align-${display['text-alignment']}`]: true,
            }"
        >
            <h3 v-if="card.byline" class="h6 text__byline">{{ card.byline }}</h3>
            <h2 v-if="card.title" class="h2 text__title">{{ card.title }}</h2>
            <DSCFText class="text__content" :content="card.text" />
            <div v-if="card.links" class="buttons">
                <NuxtLink
                    v-for="(link, key) in card.links.filter(link => link.title)"
                    :key="link.id"
                    v-bind="$cms.parseLink(link)"
                    :class="resolveClass(key)"
                >
                    <span>{{ link.title }}</span>
                </NuxtLink>
            </div>
        </div>
    </section>
</template>
<script setup>
import { computed } from 'vue';

const props = defineProps({
    contentType: {
        type: String,
        required: false,
        default: 'cardList',
    },
    internal: {
        type: [ String, null ],
        required: false,
        default: 'Text block',
    },
    display: {
        type: Object,
        required: false,
        default: () => ({
            variation: 'short',
            component: 'plain-text',
            alignment: 'center',
        }),
    },
    createdAt: {
        type: String,
        required: false,
        default: '',
    },
    updatedAt: {
        type: String,
        required: false,
        default: '',
    },
    isSchedulerEnabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    title: {
        type: [ String, null ],
        required: false,
        default: null,
    },
    cards: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const columnsAmount = computed(() => {
    const columnsMaxLength = 4;

    if (props.cards.length < columnsMaxLength) {
        return props.cards.length;
    }

    return columnsMaxLength;
});

const componentClasses = computed(() => ({
    [`columns-${columnsAmount.value}`]: true,
    [`variation-${props.display.variation || 'short'}`]: true,
}));

const resolveClass = key => ({
    'ds-btn': true,
    'ds-btn--large': true,
    [`ds-btn--${key === 0 ? 'primary' : 'secondary'}`]: true,
});
</script>
<style scoped>
.text-block {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.text-block .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.text-block {
  padding-top: 4rem;
}
.text-block .inner {
  display: flex;
  flex-flow: column wrap;
  margin: 0 auto 4rem;
}
.text-block .inner.align-left > * {
  text-align: left;
}
.text-block .inner.align-left .buttons {
  justify-content: flex-start;
}
.text-block .inner.align-center {
  justify-content: center;
}
.text-block .inner.align-center > * {
  text-align: center;
}
.text-block .inner .buttons {
  align-self: center;
  padding-top: 2rem;
  margin-top: auto;
  margin-left: -1.5rem;
  margin-bottom: -1rem;
}
.text-block .inner .buttons > * {
  display: inline-flex;
  margin-bottom: 1rem;
  margin-left: 1.5rem;
}
.text-block.columns-2:not(.variation-long) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: stretch;
  grid-gap: 0 4rem;
}
.text-block.columns-3:not(.variation-long) {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
  grid-gap: 0 2rem;
}
.text-block.columns-4:not(.variation-long) {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: stretch;
}
@media (max-width: 991px) {
  .text-block.columns-2:not(.variation-long), .text-block.columns-3:not(.variation-long), .text-block.columns-4:not(.variation-long) {
    grid-template-columns: 1fr;
    grid-gap: 4rem 0;
  }
}

:global(section.text-block + section.text-block .inner) {
  padding-top: 0;
}

:global(section.text-block + .cards-list.display-cards) {
  margin-top: 0;
}

.text-block.variation-long .inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto 1fr;
  gap: 0 2rem;
  align-items: start;
}
.text-block.variation-long .inner .h6,
.text-block.variation-long .inner .h2 {
  grid-column-start: 1;
  align-self: start;
}
.text-block.variation-long .inner .rich-text {
  grid-row-start: 1;
  grid-column-start: 2;
  grid-row-end: span 2;
}
.text-block.variation-long .inner .buttons {
  grid-row-start: 3;
  grid-column-start: 2;
}
@media (max-width: 991px) {
  .text-block.variation-long .inner {
    grid-template-columns: 1fr;
    grid-gap: 0;
  }
  .text-block.variation-long .inner > * {
    grid-column-start: 1;
  }
  .text-block.variation-long .inner .rich-text {
    grid-column-start: 1;
    grid-row-start: auto;
  }
  .text-block.variation-long .inner .buttons {
    grid-row-start: 5;
    grid-column-start: 1;
  }
}

.text-block.variation-short .inner > * {
  width: 100%;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.text-block.variation-highlight .inner .text__byline {
  display: none;
}
.text-block.variation-highlight .inner > .text__title {
  font-size: 1rem;
  margin-bottom: 2rem;
}
.text-block.variation-highlight .inner > .text__content {
  font-size: 2rem;
  line-height: 1.33;
  width: 75%;
  max-width: 925px;
}
.text-block.variation-highlight .inner > .buttons {
  align-self: start;
  gap: 1rem;
}
.text-block.variation-highlight .inner > .buttons > *:not(:first-child) {
  margin-left: 0;
}

@media (min-width: 992px) {
  .text-block.variation-highlight .inner > .text__title {
    font-size: 1.5rem;
  }
  .text-block.variation-highlight .inner > .text__content {
    font-size: 3.25rem;
  }
  .text-block.variation-highlight .inner > .buttons > * {
    margin-bottom: 0;
  }
}
</style>
